import { Box, useMediaQuery, useTheme } from '@mui/material';
import { t } from 'i18next';
import { Map as OLMap } from 'ol';
import React, { FC, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import Card from '../../../components/Card';
import CardContent from '../../../components/Card/CardContent';
import { cardClassIdentifier } from '../../../components/Card/sharedCardStyles';
import CardError from '../../../components/Error/CardError';
import GlappMap from '../../../components/Map/GlappMap';
import useMapAccessToken from '../../../components/Map/hooks/useMapAccessToken';
import SearchPostalForm from '../../../components/SearchPostalForm';
import { useCurrentActor } from '../../../hooks/useCurrentActor';
import useFontsChecker from '../../../hooks/useFontsChecker';
import { appConfig } from '../../../lib/config';
import emoji from '../../../lib/emoji';
import { BreakPoints } from '../../../theme/types';

const wrapperFullStyle = {
  [`& .${cardClassIdentifier}`]: {
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.5)',
  },
  '& p': {
    marginBottom: 0,
  },
  left: '5vw',
  marginRight: '5vw',
  position: 'absolute',
  top: '5vw',
  zIndex: 900,
};
const wrapperSmallStyle = {
  [`& > .${cardClassIdentifier}`]: {
    '& p': {
      marginBottom: 0,
    },
    boxShadow: '0 2px 5px rgba(0, 0, 0, 0.5)',
    margin: 0,
  },
  left: '1em',
  maxWidth: '100%',
  position: 'absolute',
  right: '1em',
  top: '1em',
  zIndex: 900,
};

const HomePage: FC = () => {
  const fontLoaded = useFontsChecker('normal 18px FontAwesome');
  const theme = useTheme();
  const [map, setMap] = useState<OLMap | undefined>(undefined);

  const screenIsWide = useMediaQuery(theme.breakpoints.up(BreakPoints.Medium));
  const session = useCurrentActor();

  const [selectedPostalCode, setSelectedPostalCodeRaw] = React.useState<number | undefined>(undefined);
  const setSelectedPostalCode = React.useCallback(
    (postalCode?: number) => {
      setSelectedPostalCodeRaw(postalCode);
    },
    [setSelectedPostalCodeRaw],
  );
  const [mapToken, requestMapToken] = useMapAccessToken();

  if (mapToken.error) {
    return (
      <Card>
        <CardContent endSpacing>
          <CardError caughtError={mapToken.error} onReload={requestMapToken} />
        </CardContent>
      </Card>
    );
  }

  return (
    <React.Fragment>
      <Helmet>
        <title>{appConfig('appName')}</title>
      </Helmet>
      {fontLoaded && !mapToken.loading && (
        <GlappMap
          map={map}
          selectedPostalCode={selectedPostalCode}
          setMap={setMap}
          setSelectedPostalCode={setSelectedPostalCode}
        />
      )}
      <Box sx={screenIsWide ? wrapperFullStyle : wrapperSmallStyle}>
        <Card>
          <CardContent>
            <h2>
              {emoji(
                session.firstName
                  ? t('home.greeting.withName', { name: session.firstName })
                  : t('home.greeting.withoutName'),
              )}
            </h2>
          </CardContent>
          <CardContent endSpacing>
            <div>{t('home.introMessage')}</div>
            <SearchPostalForm map={map} setSelectedPostalCode={setSelectedPostalCode} />
          </CardContent>
        </Card>
      </Box>
    </React.Fragment>
  );
};

export default HomePage;
